if(window.localStorage) {
    // Pusher, after two failed connections, semi-permanently falls back to XHR
    // this means you get stuck on the less-pleasant websocket transport if
    // you've had temporary internet connection troubles... so we reset this for
    // every *full* page load so it can retry
    window.localStorage.removeItem('pusherTransportTLS');
}

import { createApp } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import { createPinia } from 'pinia';
import { Modal, Dropdown, Carousel, Tooltip } from 'bootstrap/dist/js/bootstrap.bundle';
import App from './app.vue';
import AxiosPlugin from './plugins/axios';
// import Bugsnag from '@bugsnag/js';
// import BugsnagPluginVue from '@bugsnag/plugin-vue';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import initNotifications from './notifications';
import { i18nVue } from 'laravel-vue-i18n';

let notificationsInitialized = false;

window._ = require('lodash');

const app = createApp({});

// Bootstrap
window.bootstrap = {
    Dropdown,
    Modal,
    Carousel,
    Tooltip
};

// Echo
window.Pusher = Pusher;
let pusherConfig = {
    broadcaster: 'pusher',
    key: window.configuration.pusher.key,
    cluster: window.configuration.pusher.cluster,
    forceTLS: true,
    disableStats: true,
    encrypted: true,
};
if(window.configuration.pusher.soketi) {
    pusherConfig = { ...pusherConfig,  ...{
        wsHost: 'soketi',
        wsPort: 6001,
        wssPort: 6001,
    }}
}
window.Echo = new Echo(pusherConfig);

// Bugsnag
// Bugsnag.start({
//     apiKey: 'YOUR_API_KEY',
//     plugins: [new BugsnagPluginVue()],
// });
// const bugsnagVue = Bugsnag.getPlugin('vue');
// app.use(bugsnagVue);

// Stores (Pinia)
app.use(createPinia())

import { useUser } from './stores/user';
import { useSidebar } from './stores/sidebar';
import { usePostMaker } from './stores/post-maker';
import { useNewCreation } from './stores/new-creation';
import { usePostDetails } from './stores/post-details';
import  Cookies from 'js-cookie';

const userStore = useUser();
const sidebarStore = useSidebar();
const postMakerStore = usePostMaker();
const newCreationStore = useNewCreation();
const postDetailsStore = usePostDetails();

let languageLoaded = false;

// Routing
import routes from './routes';
export const router = createRouter({
    history: createWebHistory(),
    routes
})
router.beforeEach(async function (to, from) {
    if (to.name == 'logout') {
        return;
    }

    if (to.name == 'connections') {
        // If we're in a popup, this is an in-flow reconnect
        // so we'll need to tell the launcher we are done
        if(window.opener) {
            const bc = new BroadcastChannel('reconnect');
            bc.onmessage = (event) => {
                if(event.data === 'yesYouShould') {
                    window.close();
                }
            };
            bc.postMessage('shouldIClose');
        }
    }

    //TODO: (maybe); If we decide to use "router-link" instead of "a" to navigate to the Admin,
    // we need to detect the navigation here and force a page refresh so that the correct CSS file is used
    // However, as long as we use "a" tag, we don't need to do that.

    sidebarStore.hide();

    //Skip /api/me when user is already loaded AND not one of the pages AND not browser refresh
    if (userStore.id && !(['post maker', 'connections', 'settings', 'calendar'].includes(to.name)) && from.name !== undefined) {
        return;
    }

    //Also skip /api/me if in the calendar AND the :date param is set
    if (userStore.id && to.name == 'calendar' && to.params.date != null && from.name !== undefined) {
        return;
    }

    // Ensure the user is logged in
    return await userStore.session().then(() => {
        //i18n
        if(! languageLoaded) {
            app.use(i18nVue, {
                lang: userStore.language,
                resolve: lang => import(`../lang/php_${lang}.json`)
            });
            languageLoaded = true;
        }

        /**
         * User is logged in and on a guest page
         */
         if(
            userStore.isAuthenticated
            && to.meta !== undefined
            && to.meta.guest !== undefined
            && to.meta.guest
        ) {
            window.location = '/dashboard';
        }

        /**
         * User is not logged in and on a non-guest page
         */
        if(
            ! userStore.isAuthenticated
            && (
                (
                    to.meta === undefined
                    || to.meta.guest === undefined
                    || !to.meta.guest
                )
                || to.name === 'homepage'
            )
        ) {
            if (window.configuration.rootRedirect.indexOf('entrance') > -1) {
                let url = to.fullPath;
                Cookies.set('intendedRoute', to.fullPath, {
                    path: '/',
                    secure: true,
                });
            } else {
                localStorage.setItem('intendedRoute', to.fullPath);
            }

            window.location = window.configuration.rootRedirect;
        }

        /**
         * User is logged and has not connected to Websockets
         */
        if(
            userStore.isAuthenticated
            && ! ('App.Models.User.' + userStore.id in window.Echo.connector.pusher.channels.channels)
            && ! notificationsInitialized
        ) {
            initNotifications(userStore, postMakerStore, newCreationStore, postDetailsStore);
            notificationsInitialized = true;
        }

        if (userStore.needsOnboarding) {
            window.location.replace('/onboarding');
            return;
        }

        if (
             userStore.hasTemporaryPassword
             && to.name != 'settings'
        ) {
            //keep sending user into settings until they update the password
            router.push({ name: 'settings'});
            return;
        }

        // User is logged in and is an Assistant
        if(userStore.company.has_assistants && userStore.assistant !== undefined) {
            if(['connections', 'settings'].includes(to.name)) {
                router.push({ name: 'dashboard'});
                return;
            }
        }

        return;
    }).catch(error => {
        console.log("Err: ", error);
    });
});
app.use(router);

// Global tools
app.use(AxiosPlugin);

// Our Global components
import Widget from './components/global/widget.vue';
import ModalComponent from './components/global/modal.vue';
import Alert from './components/global/alert.vue';
import Sidebar from './components/global/sidebar.vue';
import AsyncStatus from './components/global/async-status.vue';
import DropdownComponent from './components/global/dropdown.vue';
import Checkbox from './components/global/checkbox.vue';
import Spinner from './components/global/spinner.vue';
import Announcements from './components/global/announcements.vue';

app.component('widget', Widget);
app.component('modal', ModalComponent);
app.component('alert', Alert);
app.component('sidebar', Sidebar);
app.component('async-status', AsyncStatus);
app.component('dropdown', DropdownComponent);
app.component('checkbox', Checkbox);
app.component('spinner', Spinner);
app.component('announcements', Announcements);

// 3rd party Global components
import vSelect from 'vue-select';
import { DatePicker } from 'v-calendar';
import { DateTime, Interval } from "luxon";

app.component('v-select', vSelect);
app.component('date-picker', DatePicker);
window.DateTime = DateTime;
window.Interval = Interval;

// Global mixins
import AlertMixin from './mixins/alert';
import SidebarMixin from './mixins/sidebar';
import ModalMixin from './mixins/modal';

app.mixin(AlertMixin);
app.mixin(SidebarMixin);
app.mixin(ModalMixin);

app.mixin({
  methods: {
    numberSpellout: function (number) {
      let numbers = ['zero', 'one', 'two', 'three', 'four', 'five', 'six'];

      return numbers[number];
    },
  },
});

// Directives
import debounce from './directives/debounce';
app.directive('debounce', (el,binding) => debounce(el,binding));

import BtnLoading from './directives/btnloading';
app.directive('btnloading', BtnLoading);

window.Vapor = require('laravel-vapor');
app.mixin({
    methods: {
        asset: window.Vapor.asset
    },
});

// Mounting The app
const appInstance = app.component('app', App)
    .mount('#app');

// Instance-required configuration
AxiosPlugin.configure(appInstance);
